// hello!! if you're coming from wl-farmers-auto (or some other repo) and you're here because you've got an error,
// please use `vehicleCoverageOnChangeWrapperFormik` from `vehicleCoverageOnChangeWrapperFormik.ts` (or consider changing your app to use react hook form)

import { AUTOMATIC_OEM_ENABLED_VEHICLES } from '../../constants/oemEnabledVehicles';
import {
  ERegionCode,
  IPolicyVehicle,
  IVehicle,
  TCoverageSideEffectsType,
  TCoverageUpdateType,
} from '../../interfaces';
import { REGION_COVERAGE_RULES } from '../constants/regionConfigs';

import { calculateVehicleAge } from './calculateVehicleAge';
import { getIsTruthy } from './getIsTruthy';
import { getOemHiddenVehicleRegionRules } from './regionUtilities/hiddenVehicleRegionRules';

export type TVehicleCoverageFormValues = {
  collision_deductible: string;
} & Partial<{
  new_car_replacement: string;
  oem_parts: string;
  towing_road_services: string;
  comprehensive_deductible: string;
  collision_deductible_waiver: string;
  rental_reimbursement: string;
  additional_equipment: string;
  gap: string;
  glass: string;
  loan_lease_payoff: string;
  loss_of_use: string;
  ridesharing: string;
  side_hustle: string;
  temporary_ride: string;
  uninsured_motorist_property_damage_limit: string;
  uninsured_motorist_property_damage_deductible: string;
}>;

export type TCoverageValues =
  | 'gap'
  | 'glass'
  | 'loan_lease_payoff'
  | 'loss_of_use'
  | 'new_car_replacement'
  | 'oem_parts'
  | 'ridesharing'
  | 'extraordinary_medical'
  | 'side_hustle'
  | 'temporary_ride'
  | 'towing_road_services'
  | 'comprehensive_deductible'
  | 'collision_deductible'
  | 'collision_deductible_waiver'
  | 'additional_equipment'
  | 'rental_reimbursement'
  | 'uninsured_motorist_property_damage_limit'
  | 'uninsured_motorist_property_damage_deductible';

export type TVehicleCoverageOnChangeWrapper = {
  onChange?: (name: TCoverageValues, value: string) => void;
  formValues: TVehicleCoverageFormValues;
  setValue: (
    field: TCoverageValues,
    value: string,
    options?: {
      shouldValidate?: boolean;
      shouldDirty?: boolean;
      shouldTouch?: boolean;
    }
  ) => void;
  setPolicyValue?: (
    field: TCoverageValues,
    value: string,
    options?: {
      shouldValidate?: boolean;
      shouldDirty?: boolean;
      shouldTouch?: boolean;
    }
  ) => void;
  bodilyInjuryCoverageValue: number;
  uninsuredMotoristPropertyDamageDeductibleValue?: number;
  callback: ({
    updateType,
    undo,
    sideEffectContext,
  }: {
    updateType: TCoverageUpdateType;
    undo?: () => void;
    sideEffectContext?: TCoverageSideEffectsType;
  }) => void;
  regionCode?: ERegionCode;
  enabledSideEffects?: string[];
  vehicleIndex?: number;
  vehicles?: (IVehicle | IPolicyVehicle)[];
  policyEffectiveDate: string;
};

export function vehicleCoverageOnChangeWrapper({
  onChange,
  formValues,
  setValue,
  setPolicyValue,
  bodilyInjuryCoverageValue,
  uninsuredMotoristPropertyDamageDeductibleValue,
  callback,
  regionCode = 'SC',
  enabledSideEffects = [],
  vehicleIndex = 0,
  vehicles = [],
  policyEffectiveDate,
}: TVehicleCoverageOnChangeWrapper) {
  return function (targetName: TCoverageValues, targetValue: string) {
    const {
      VUMPD_REQUIRES_NO_COMP_COLL,
      COMP_COLL_REQUIRED_SEPARATELY,
      UMPD_DEDUCTIBLE_REQUIRES_NO_COMP_COLL,
    } = REGION_COVERAGE_RULES;

    onChange?.(targetName, targetValue);

    const valueCoverageKeys: (keyof TVehicleCoverageFormValues)[] = [
      'oem_parts',
      'new_car_replacement',
      'towing_road_services',
      'loan_lease_payoff',
      'loss_of_use',
      'gap',
      'temporary_ride',
      'side_hustle',
      'uninsured_motorist_property_damage_limit',
      'glass',
    ];

    const isOneOrMoreCollisionDeductibleDependentFieldsIncluded =
      valueCoverageKeys.filter((vehicleCoverageKey) => {
        const value = formValues?.[vehicleCoverageKey];
        return (
          vehicleCoverageKey !== 'uninsured_motorist_property_damage_limit' &&
          value !== 'null' &&
          value !== 'false' &&
          !!value
        );
      }).length > 0;

    const isCEQIncluded = getIsTruthy(formValues?.additional_equipment);

    const isCDWIncluded = getIsTruthy(formValues?.collision_deductible_waiver);

    const isRENTIncluded = getIsTruthy(formValues?.rental_reimbursement);

    const areRentCdwCeqIncluded =
      isRENTIncluded && isCDWIncluded && isCEQIncluded;
    const areCdwCeqIncluded = isCDWIncluded && isCEQIncluded && !isRENTIncluded;
    const areRentCeqIncluded =
      isRENTIncluded && isCEQIncluded && !isCDWIncluded;

    const currentCollisionDeductible = formValues?.collision_deductible;
    const towingRoadServicesValue = formValues?.towing_road_services;
    const lossOfUseValue =
      formValues?.loss_of_use || formValues?.temporary_ride;

    const isCollisionDeductibleNotIncludedOrSelectOne =
      currentCollisionDeductible === '' ||
      currentCollisionDeductible === 'null';

    const isTowingRoadServicesNotIncludedOrSelectOne =
      towingRoadServicesValue === '' ||
      towingRoadServicesValue === 'null' ||
      towingRoadServicesValue === 'false';

    const isLossOfUseNotIncludedOrSelectOne =
      lossOfUseValue === '' || lossOfUseValue === 'null';

    function undo() {
      Object.entries(formValues).forEach(([key, value]) => {
        setValue(key as TCoverageValues, value, { shouldValidate: true });
      });
    }
    switch (targetName) {
      case 'oem_parts':
      case 'new_car_replacement':
      case 'towing_road_services':
      case 'side_hustle':
      case 'loan_lease_payoff':
      case 'loss_of_use':
      case 'gap':
      case 'temporary_ride':
        if (
          getIsTruthy(targetValue) &&
          isCollisionDeductibleNotIncludedOrSelectOne
        ) {
          setValue('collision_deductible', '100', { shouldValidate: true });
          callback({
            updateType: 'COMPREHENSIVE_COLLISION_COVERAGE_UPDATE',
            undo,
          });
        }
        break;

      case 'comprehensive_deductible':
        if (COMP_COLL_REQUIRED_SEPARATELY.includes(regionCode)) {
          const collisionVal = formValues?.collision_deductible;
          if (isRENTIncluded) {
            setValue('rental_reimbursement', 'null');

            callback({
              updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
              undo,
              sideEffectContext: {
                type: `VEHICLE_ADD_ONS_REMOVED`,
                name: 'Rental Reimbursement',
                isIncreased: false,
                isRemoved: true,
                cause: `comprehensive_deductible`,
                causeName: 'Comprehensive',
                vehicleIndex,
              },
            });
          }

          if (!getIsTruthy(targetValue)) {
            if (!getIsTruthy(collisionVal) && isCEQIncluded) {
              // if ceq is included, but collision was not, and now comprehensive (targetVal) isn't either, set ceq to null.
              setValue('additional_equipment', 'null');
              callback({
                updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
                undo,
                sideEffectContext: {
                  type: `VEHICLE_ADD_ONS_REMOVED`,
                  name: 'Additional Equipment',
                  isIncreased: false,
                  isRemoved: true,
                  cause: `comprehensive_deductible`,
                  causeName: 'Comprehensive',
                  vehicleIndex,
                },
              });
            }
          } else if (getIsTruthy(targetValue) && !isCEQIncluded) {
            // if comprehensive exists and additional equipment wasn't already included, set ceq and default to 1000
            setValue('additional_equipment', '1000');
          }
        }

        break;
      case 'collision_deductible':
        if (
          UMPD_DEDUCTIBLE_REQUIRES_NO_COMP_COLL.includes(regionCode) &&
          getIsTruthy(targetValue) &&
          uninsuredMotoristPropertyDamageDeductibleValue
        ) {
          setPolicyValue?.(
            'uninsured_motorist_property_damage_deductible',
            'null'
          );
          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            undo,
            sideEffectContext: {
              type: `UMPD_DEDUCTIBLE_UPDATE`,
              name: 'Uninsured / Underinsured Motorist Property Damage Deductible',
              isIncreased: false,
              isRemoved: true,
              cause: `collision_deductible`,
              causeName: 'Comprehensive',
              vehicleIndex,
            },
          });
        }
        const vehicle = vehicles[vehicleIndex];
        if (getIsTruthy(targetValue)) {
          if (enabledSideEffects.includes('ENABLE_OEM_ON_COMP_COLL')) {
            const vehicleAge = calculateVehicleAge(vehicle);
            const notHiddenOemCoverageForVehicle =
              !getOemHiddenVehicleRegionRules(regionCode, vehicleAge);
            if (
              AUTOMATIC_OEM_ENABLED_VEHICLES.toyota.includes(vehicle?.make) &&
              notHiddenOemCoverageForVehicle
            ) {
              setValue('oem_parts', 'true', { shouldValidate: true });
            }
            callback({
              updateType: 'ADDON_ENABLED',
              undo,
              sideEffectContext: {
                type: `OEM_ADDON_ENABLED`,
                name: 'dependent',
                isIncreased: false,
                isRemoved: false,
                cause: `collision_deductible`,
                causeName: 'Comprehensive & Collision',
                vehicleIndex,
              },
            });
          }
        }

        if (COMP_COLL_REQUIRED_SEPARATELY.includes(regionCode)) {
          const comprehensiveVal = formValues?.comprehensive_deductible;
          if (
            enabledSideEffects.includes('ENABLE_CEQ_ON_COMP_OR_COLL') &&
            getIsTruthy(targetValue) &&
            !isCEQIncluded
          ) {
            // if targetValue exists and config is set up with this enable-ceq requirement, dynamically set the value of ceq (additional equipment) and default it to 1000.
            setValue('additional_equipment', '1000');

            // this is where you could also add callback if toggle ever comes back and tells us we do, in fact, need a side effect text alert for having dynamically added ceq (currently not in AC)
          } else if (!getIsTruthy(targetValue)) {
            if (isRENTIncluded) {
              // remove RENT if turning off coll, regardless of other params, since RENT requires both comp and coll.
              setValue('rental_reimbursement', 'null');
            }
            if (isCDWIncluded) {
              setValue('collision_deductible_waiver', 'false');
            }
            if (getIsTruthy(comprehensiveVal)) {
              if (areRentCdwCeqIncluded) {
                callback({
                  updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
                  undo,
                  sideEffectContext: {
                    type: `VEHICLE_ADD_ONS_REMOVED`,
                    name: 'Rental Reimbursement and Waiver of Collision Deductible',
                    isIncreased: false,
                    isRemoved: true,
                    cause: `collision_deductible`,
                    causeName: 'Collision',
                    vehicleIndex,
                  },
                });
              } else if (areCdwCeqIncluded) {
                callback({
                  updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
                  undo,
                  sideEffectContext: {
                    type: `VEHICLE_ADD_ONS_REMOVED`,
                    name: 'Waiver of Collision Deductible',
                    isIncreased: false,
                    isRemoved: true,
                    cause: `collision_deductible`,
                    causeName: 'Collision',
                    vehicleIndex,
                  },
                });
              } else if (areRentCeqIncluded) {
                callback({
                  updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
                  undo,
                  sideEffectContext: {
                    type: `VEHICLE_ADD_ONS_REMOVED`,
                    name: 'Rental Reimbursement',
                    isIncreased: false,
                    isRemoved: true,
                    cause: `collision_deductible`,
                    causeName: 'Collision',
                    vehicleIndex,
                  },
                });
              }
            } else if (!getIsTruthy(comprehensiveVal)) {
              // if comprehensive was not included and collision is now being turned off, by definition additional equipment had to have also existed
              // and now needs to also by definition be turned off
              // no scenario in which RENT can be removed here because RENT cannot have existed if comprehensive was already off before turning collision off
              setValue('additional_equipment', 'null');
              if (isCDWIncluded) {
                // the callback message will specifically include both dependent (cdw) and additional equipment (ceq) being removed.
                callback({
                  updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
                  undo,
                  sideEffectContext: {
                    type: `VEHICLE_ADD_ONS_REMOVED`,
                    name: 'Waiver of Collision Deductible and Additional Equipment',
                    isIncreased: false,
                    isRemoved: true,
                    cause: `collision_deductible`,
                    causeName: 'Collision',
                    vehicleIndex,
                  },
                });
              } else {
                // but if no prior cdw, the message will specifically mention only Additional Equipment being removed.
                callback({
                  updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
                  undo,
                  sideEffectContext: {
                    type: `VEHICLE_ADD_ONS_REMOVED`,
                    name: 'Additional Equipment',
                    isIncreased: false,
                    isRemoved: true,
                    cause: `collision_deductible`,
                    causeName: 'Collision',
                    vehicleIndex,
                  },
                });
              }
            }
          }
        } else if (
          // implied !COMP_COLL_REQUIRED_SEPARATELY.includes(regionCode) &&
          isOneOrMoreCollisionDeductibleDependentFieldsIncluded &&
          !getIsTruthy(targetValue)
        ) {
          valueCoverageKeys.forEach((vehicleCoverageKey) => {
            if (
              vehicleCoverageKey === 'uninsured_motorist_property_damage_limit'
            ) {
              return;
            }
            setValue(
              vehicleCoverageKey,
              vehicleCoverageKey === 'loss_of_use' ||
                vehicleCoverageKey === 'temporary_ride'
                ? 'null'
                : 'false',
              { shouldValidate: true }
            );
          });

          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            undo,
            sideEffectContext: {
              type: `VEHICLE_ADD_ONS_REMOVED`,
              name: 'dependent',
              isIncreased: false,
              isRemoved: true,
              cause: `collision_deductible`,
              causeName: 'Comprehensive & Collision',
              vehicleIndex,
            },
          });
        } else if (
          bodilyInjuryCoverageValue >= 100000 &&
          (isTowingRoadServicesNotIncludedOrSelectOne ||
            isLossOfUseNotIncludedOrSelectOne)
        ) {
          setValue('towing_road_services', 'true');

          if (formValues.loss_of_use) {
            setValue('loss_of_use', '30');
          }
          if (formValues.temporary_ride) {
            setValue('temporary_ride', '30');
          }
          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            undo,
          });
        } else if (
          VUMPD_REQUIRES_NO_COMP_COLL.includes(regionCode) &&
          getIsTruthy(targetValue) &&
          getIsTruthy(formValues.uninsured_motorist_property_damage_limit)
        ) {
          setValue('uninsured_motorist_property_damage_limit', 'null');
          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            sideEffectContext: {
              type: `VEHICLE_UMPD_UPDATE`,
              name: 'Uninsured Motorist Property Damage',
              isIncreased: false,
              isRemoved: true,
              cause: `collision_deductible`,
              causeName: 'Comprehensive & Collision',
              vehicleIndex,
            },
          });
        }

        break;
      default:
        break;
    }
  };
}
